import React from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { toast } from 'react-toastify';
import axiosInstance from '../../../config/axios.config';
import { SmartHeader, SmartField, NoRecordsCard } from './_SmartFieldRenderer';

const Section = ({ icon, title, children }) => (
    <div className="pt-3 pb-3 border-bottom-1 surface-border">
        <div className="flex align-items-center gap-2 mb-3">
            <i className={`pi ${icon} text-primary`} aria-hidden="true" />
            <span className="text-sm font-semibold text-900 uppercase">{title}</span>
        </div>
        <div className="grid">{children}</div>
    </div>
);

const formatGender = (g) => (g === 'M' ? 'Male' : g === 'F' ? 'Female' : g);

const CAMembershipOutputComponent = ({
    reportData,
    onDownloadPDF,
    pdfDownloadUrl,
    actualServiceDetails,
    serviceDetails,
    hideCredits,
    isSharedView,
}) => {
    const shouldHideCredits = hideCredits || isSharedView;

    const parsedData =
        typeof reportData === 'string'
            ? (() => {
                  try { return JSON.parse(reportData); } catch { return reportData; }
              })()
            : reportData;

    const pdfPasswordHint =
        parsedData?.pdf_password_hint ||
        reportData?.pdf_password_hint ||
        parsedData?.data?.pdf_password_hint ||
        reportData?.data?.pdf_password_hint ||
        null;

    if (reportData?.code === '1004') {
        return <NoRecordsCard subtitle="No data found for the entered CA membership number. Please enter valid details." />;
    }

    let caResponse = null;
    let errorMessage = null;

    if (reportData?.success === false) {
        errorMessage = reportData.message || 'CA membership verification failed';
        caResponse = reportData.response || reportData.data || {};
    } else if (reportData?.report_json?.data?.membership_details) caResponse = reportData.report_json.data.membership_details;
    else if (reportData?.report_json?.response) caResponse = reportData.report_json.response;
    else if (reportData?.data?.membership_details) caResponse = reportData.data.membership_details;
    else if (reportData?.data?.response) caResponse = reportData.data.response;
    else if (reportData?.success && reportData?.response?.response) caResponse = reportData.response.response;
    else if (reportData?.api_response?.response?.response) caResponse = reportData.api_response.response.response;
    else if (reportData?.response?.response) caResponse = reportData.response.response;
    else if (reportData?.response) caResponse = reportData.response;
    else caResponse = reportData;

    if (caResponse?.error || caResponse?.statusCode === 'error') {
        errorMessage = caResponse?.message || caResponse?.error || 'CA membership verification failed';
    }

    const verificationStatus = errorMessage
        ? false
        : caResponse?.isValid !== undefined
        ? caResponse.isValid
        : caResponse?.valid !== undefined
        ? caResponse.valid
        : caResponse?.code === 200;

    if (errorMessage && (!caResponse || Object.keys(caResponse).length === 0)) {
        return (
            <Card>
                <div className="text-center mb-3">
                    <i className="pi pi-times-circle text-5xl text-red-500 block mb-3" aria-hidden="true" />
                    <h3 className="text-lg font-semibold text-900 m-0">CA Membership Verification Failed</h3>
                </div>
                <Message severity="error" text={errorMessage} className="w-full mb-3" />
                <p className="text-700 text-sm m-0 mb-3">Please check the CA membership details and try again.</p>
                <p className="text-500 text-sm m-0 mb-1">Common reasons for verification failure:</p>
                <ul className="text-500 text-sm m-0 pl-4">
                    <li>Invalid CA membership number</li>
                    <li>Membership number does not exist in ICAI database</li>
                    <li>Network or service connectivity issues</li>
                    <li>Insufficient credits in your wallet</li>
                </ul>
            </Card>
        );
    }

    const handleDownloadPDF = async () => {
        if (pdfPasswordHint) {
            toast.info(pdfPasswordHint, { position: 'top-center', autoClose: 15000 });
        }

        if (onDownloadPDF && typeof onDownloadPDF === 'function') {
            onDownloadPDF();
            return;
        }

        const downloadLink =
            pdfDownloadUrl ||
            reportData?.report_json?.data?.pdf_download_url ||
            reportData?.report_json?.pdf_download_url ||
            reportData?.pdf_url ||
            reportData?.pdf_download_url ||
            reportData?.download_link ||
            reportData?.download_url ||
            reportData?.data?.pdf_url ||
            reportData?.data?.pdf_download_url ||
            reportData?.data?.download_link ||
            reportData?.response?.pdf_download_url ||
            reportData?.api_response?.pdf_download_url;

        if (!downloadLink) {
            toast.error('PDF download URL not available');
            return;
        }

        try {
            toast.info('Preparing PDF download...');
            if (downloadLink.startsWith('http')) {
                window.open(downloadLink, '_blank', 'noopener,noreferrer');
                toast.success('Download started');
                return;
            }
            const urlObj = new URL(downloadLink, window.location.origin);
            const apiPath = urlObj.pathname;
            const response = await axiosInstance.get(apiPath, {
                responseType: 'blob',
                headers: { Accept: 'application/pdf' },
            });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `ca_membership_verification_${Date.now()}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('PDF downloaded successfully');
        } catch (err) {
            const status = err.response?.status;
            const msg =
                status === 500 ? 'Server error while generating PDF. Please try again.' :
                status === 404 ? err.response?.data?.message || 'Report not found or associated data is missing.' :
                err.response?.data?.message || 'Failed to download PDF';
            toast.error(msg);
        }
    };

    const remainingBalance = actualServiceDetails?.available_credits || serviceDetails?.available_credits;
    const hasPdf = !!(
        pdfDownloadUrl ||
        reportData?.report_json?.data?.pdf_download_url ||
        reportData?.report_json?.pdf_download_url ||
        reportData?.pdf_url ||
        reportData?.pdf_download_url ||
        reportData?.data?.pdf_url ||
        reportData?.data?.pdf_download_url ||
        reportData?.api_response?.pdf_download_url
    );

    return (
        <div>
            <SmartHeader title="CA Membership Verification Report" icon="pi-briefcase" remainingBalance={remainingBalance} shouldHideCredits={shouldHideCredits} />

            {errorMessage && (
                <div className="mb-3">
                    <Message severity="error" text={errorMessage} className="w-full" />
                </div>
            )}

            <Card>
                <Section icon="pi-user" title="Personal Details">
                    <SmartField name="full_name" value={caResponse?.name} />
                    <SmartField name="gender" value={formatGender(caResponse?.gender)} />
                    <SmartField name="mobile_number" value={caResponse?.mobileNo} />
                    <SmartField name="email" value={caResponse?.email} />
                </Section>

                <Section icon="pi-briefcase" title="Professional Information">
                    <SmartField name="qualification" value={caResponse?.Qualification} />
                    <SmartField name="associate_year" value={caResponse?.AssociateYear} />
                    {caResponse?.FellowYear && <SmartField name="fellow_year" value={caResponse.FellowYear} />}
                    {caResponse?.COPStatus && <SmartField name="cop_status" value={caResponse.COPStatus} />}
                    {caResponse?.memberStatus && <SmartField name="member_status" value={caResponse.memberStatus} />}
                    <div className="col-12 sm:col-6 lg:col-4 flex flex-column gap-1">
                        <span className="text-500 text-xs font-medium">Verification Status</span>
                        <span><Tag value={verificationStatus ? 'Verified' : 'Not Verified'} severity={verificationStatus ? 'success' : 'warning'} /></span>
                    </div>
                </Section>

                {caResponse?.address && (
                    <Section icon="pi-map-marker" title="Address & Contact">
                        <div className="col-12 flex flex-column gap-1">
                            <span className="text-500 text-xs font-medium">Complete Address</span>
                            <span className="text-900 text-sm font-semibold" style={{ wordBreak: 'break-word' }}>{caResponse.address}</span>
                        </div>
                    </Section>
                )}

                <div className="text-center pt-3">
                    <Button
                        label="Download CA Membership Report"
                        icon="pi pi-download"
                        onClick={handleDownloadPDF}
                        disabled={!hasPdf}
                        size="small"
                    />
                    {pdfPasswordHint && (
                        <div className="mt-2">
                            <Message severity="info" text={pdfPasswordHint} />
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default CAMembershipOutputComponent;
